import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ChevronDownIcon from '@/assets/icons/chevron-lg-down.svg';
import ChevronRightIcon from '@/assets/icons/chevron-right.svg';
import DuplicateIcon from '@/assets/icons/duplicate.svg';
import { TrashIcon } from '@/components/icons/trash';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';

import { AudienceMessageForm } from './audience-message-form';
import { usePoolForm } from './use-pool-form';

interface OpeningCardProps {
  openingId: string;
  isFirstOpening: boolean;
}

export function OpeningCard({ openingId, isFirstOpening }: OpeningCardProps) {
  const intl = useIntl();
  const { removeOpeningFromPool } = usePoolForm();
  const { hookForm } = useJobPostingContext();
  const { getValues, watch, setValue } = hookForm;

  const [isExpanded, setIsExpanded] = useState(true);

  const openingTitle = getValues(`jobDetails.${openingId}.title`);
  const audienceId = watch(`pool.openingMessages.${openingId}.audienceId`);
  const subject = watch(`pool.openingMessages.${openingId}.subject`);
  const body = watch(`pool.openingMessages.${openingId}.body`);

  const enabledDuplicateButton = isFirstOpening && !!audienceId && !!body;

  const handleDuplicateClick = () => {
    const poolOpenings = getValues('pool.openingMessages');
    const newPoolOpenings = { ...poolOpenings };
    const messageData = {
      audienceId,
      subject,
      body,
    };

    // Copy to all openings
    Object.keys(newPoolOpenings).forEach(id => {
      newPoolOpenings[id] = {
        ...newPoolOpenings[id],
        ...messageData,
      };
    });

    setValue('pool.openingMessages', newPoolOpenings, { shouldValidate: true });
  };

  return (
    <div className="border-fx-neutral-200 mb-3 w-[640px] rounded-lg border bg-white py-6 px-12 shadow-sm">
      <div className="flex flex-1 flex-col gap-6 ">
        <div className="flex items-center justify-between gap-2">
          {/* Opening title */}
          <div className="flex flex-col gap-1">
            <Typography variant="caption" className="!text-fx-neutral-500">
              <FormattedMessage
                defaultMessage="Opening"
                description="Opening section title"
              />
            </Typography>
            <Typography variant="body2">{openingTitle}</Typography>
          </div>

          <div className="flex items-center gap-1">
            {/* Remove opening button */}
            <Tooltip
              content={intl.formatMessage({
                defaultMessage: 'Remove opening',
                description: 'Remove opening button tooltip',
              })}
            >
              <Button
                type="button"
                variant="icon"
                size="icon"
                onClick={() => removeOpeningFromPool(openingId)}
              >
                <TrashIcon />
              </Button>
            </Tooltip>

            {/* Expand/collapse button */}
            <Button
              type="button"
              variant="icon"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <img
                src={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                alt="Expand"
              />
            </Button>
          </div>
        </div>

        {isExpanded && <AudienceMessageForm openingId={openingId} />}

        {/* Duplicate Audience and Message button */}
        {isFirstOpening && (
          <Button
            variant="ghost"
            type="button"
            className="self-start"
            disabled={!enabledDuplicateButton}
            onClick={handleDuplicateClick}
          >
            <img src={DuplicateIcon} alt="Duplicate" />
            <FormattedMessage
              defaultMessage="Copy this audience and message to all other openings"
              description="Button to copy audience and message to all other openings"
            />
          </Button>
        )}
      </div>
    </div>
  );
}
